import React from "react";
import { useMsal } from "@azure/msal-react";
import {
  Button,
  Card,
  Field,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  Folder24Regular,
  ShieldCheckmark24Regular,
} from "@fluentui/react-icons";
import { Page } from "../components/App";
import { getAccessToken } from "../services/authService";
import { ensureAppFolder, AppFolderInfo } from "../services/fileService";
import { taskPaneBackground } from "../theme";

// Styling hook keeps layout consistent with the rest of the task pane pages.
const useStyles = makeStyles({
  root: {
    height: "88vh",
    display: "flex",
    flexDirection: "column",
    ...taskPaneBackground,
    ...shorthands.padding("16px"),
    boxSizing: "border-box",
    ...shorthands.gap("12px"),
    overflow: "hidden",
  },
  headerCard: {
    display: "flex",
    flexShrink: 0,
    alignItems: "flex-start",
    ...shorthands.gap("12px"),
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding("20px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  headerIcon: {
    display: "flex",
    width: "40px",
    height: "40px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: "#7e22ce",
    ...shorthands.border("1px", "solid", "#e9d5ff"),
    ...shorthands.borderRadius("12px"),
    backgroundColor: "#faf5ff",
  },
  eyebrow: {
    margin: 0,
    color: "#7e22ce",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: {
    marginTop: "2px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: "22px",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1.2",
    letterSpacing: "-0.025em",
  },
  description: {
    marginTop: "6px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "20px",
  },
  card: {
    display: "flex",
    minHeight: 0,
    flex: 1,
    flexDirection: "column",
    ...shorthands.gap("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding("20px"),
    boxShadow: tokens.shadow4,
    overflowY: "auto",
  },
  cardHeading: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
  },
  iconBadge: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  cardTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  cardSubtitle: {
    marginTop: "2px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  informationPanel: {
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("12px", "14px"),
    color: tokens.colorNeutralForeground2,
    backgroundColor: "#f5f9ff",
    fontSize: tokens.fontSizeBase300,
    lineHeight: "20px",
  },
  setupButton: {
    width: "100%",
    boxShadow: "none",
  },
  identifiers: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("12px", "14px"),
    backgroundColor: tokens.colorNeutralBackground2,
    overflowWrap: "anywhere",
  },
  footer: {
    marginTop: "auto",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: tokens.shadow4,
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    ...shorthands.gap("8px"),
  },
  footerButton: {
    minWidth: 0,
    flex: 1,
    boxShadow: "none",
  },
  backButton: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    ":hover": { backgroundColor: tokens.colorNeutralBackground2 },
  },
});

interface SetupPageProps {
  onNavigate: (page: Page) => void;
}

export const SetupPage: React.FC<SetupPageProps> = ({ onNavigate }) => {
  const styles = useStyles();
  const { instance } = useMsal();

  // Local state tracks loading, success payload, and friendly error copy.
  const [isLoading, setIsLoading] = React.useState(false);
  const [result, setResult] = React.useState<AppFolderInfo | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleSetup = async () => {
    setIsLoading(true);
    setResult(null);
    setError(null);
    try {
      const token = await getAccessToken(instance); // Acquire backend-scoped token for the API call.
      const folder = await ensureAppFolder(token); // Ask the backend to ensure the app folder exists.
      setResult(folder);
    } catch (setupError: any) {
      // Surface the message returned by the backend or fall back to a generic copy.
      const message =
        setupError?.message || "We could not set up the Sourcestone folder. Please try again.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const resolvedPath = result?.path || "Apps/Sourcestone";

  return (
    <div className={styles.root}>
      <Card className={styles.headerCard}>
        <div className={styles.headerIcon} aria-hidden>
          <Folder24Regular />
        </div>
        <div>
          <p className={styles.eyebrow}>Workspace setup</p>
          <h1 className={styles.title}>Set up the Sourcestone folder</h1>
          <p className={styles.description}>
            Sourcestone keeps everything in a private OneDrive workspace. Use the button below to
            create the folder (or check that it already exists) before starting a job.
          </p>
        </div>
      </Card>

      <Card className={styles.card}>
        <div className={styles.cardHeading}>
          <div className={styles.iconBadge} aria-hidden>
            <ShieldCheckmark24Regular />
          </div>
          <div>
            <h2 className={styles.cardTitle}>OneDrive workspace</h2>
            <p className={styles.cardSubtitle}>Private storage within your organisation</p>
          </div>
        </div>

        <div className={styles.informationPanel}>
          We’ll create <strong>{resolvedPath}</strong> under your OneDrive account (or confirm it’s
          already there). Once that folder exists, every job you run will stay inside it. Share the
          Drive ID and Item ID below if support ever needs to help you troubleshoot.
        </div>
        <Button
          appearance="primary"
          className={mergeClasses(styles.setupButton, "sourcestone-navigation-primary")}
          size="large"
          onClick={handleSetup}
          disabled={isLoading}
        >
          Set up Sourcestone folder
        </Button>
        {isLoading && <Spinner label="Setting up your OneDrive workspace…" />}

        {result && (
          <MessageBar intent={result.created ? "success" : "info"}>
            <MessageBarTitle>
              {result.created ? "Folder created" : "Folder already existed"}
            </MessageBarTitle>
            <MessageBarBody>
              We {result.created ? "created" : "found"} the folder at{" "}
              <strong>{resolvedPath}</strong>.{" "}
              {result.webUrl && (
                <Link href={result.webUrl} target="_blank" rel="noopener noreferrer">
                  Open in OneDrive
                </Link>
              )}
            </MessageBarBody>
          </MessageBar>
        )}

        {error && (
          <MessageBar intent="error">
            <MessageBarTitle>We couldn’t complete setup</MessageBarTitle>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.identifiers}>
          <Field
            label="Drive identifiers"
            validationMessage="Share these values with Sourcestone support if needed."
          >
            <span>
              Drive ID: {result?.driveId ?? "n/a"}
              <br />
              Item ID: {result?.itemId ?? "n/a"}
            </span>
          </Field>
        </div>
      </Card>

      <Card className={styles.footer}>
        <div className={styles.footerButtons}>
          <Button
            appearance="primary"
            className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowLeft24Regular />}
            onClick={() => onNavigate("taskSelection")}
          >
            Back to tasks
          </Button>
          <Button
            appearance="primary"
            className={mergeClasses(styles.footerButton, "sourcestone-navigation-primary")}
            disabled
          >
            Next (coming soon)
          </Button>
        </div>
      </Card>
    </div>
  );
};
